/**
 * SHOP TIME MODULE FOR EDGE FUNCTIONS
 *
 * CRITICAL: This module only performs UTC → shop-local conversions (for validation/display)
 * For local → UTC conversions, the frontend must send precomputed UTC instants
 *
 * This ensures identical behavior between frontend and edge functions,
 * since date-fns-tz (used on frontend) and Intl API may differ on DST edge cases.
 */

import { clock } from './clock.ts';

/**
 * Convert a UTC ISO string to shop-local date label (YYYY-MM-DD)
 * Uses Intl.DateTimeFormat for Deno compatibility
 * 
 * @param utcIso - UTC ISO string (e.g., "2025-03-09T07:30:00.000Z")
 * @param shopTz - IANA timezone (e.g., "America/Los_Angeles")
 * @returns Date string in YYYY-MM-DD format in shop timezone
 */
export function getShopDayLabel(utcIso: string, shopTz: string): string {
  try {
    const date = new Date(utcIso);
    if (isNaN(date.getTime())) {
      console.warn('[shopTime] Invalid UTC date:', utcIso);
      return formatDateToLabel(new Date());
    }
    
    const formatter = new Intl.DateTimeFormat('en-CA', {
      timeZone: shopTz,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
    
    // en-CA locale gives us YYYY-MM-DD format directly
    return formatter.format(date);
  } catch (error) {
    console.error('[shopTime] getShopDayLabel error:', error);
    return formatDateToLabel(new Date());
  }
}

/**
 * Convert UTC ISO string to shop-local time components
 * 
 * @param utcIso - UTC ISO string
 * @param shopTz - IANA timezone
 * @returns Object with dateStr, timeStr, and minutesSinceMidnight
 */
export function utcToShopLocal(utcIso: string, shopTz: string): {
  dateStr: string;
  timeStr: string;
  minutesSinceMidnight: number;
} {
  try {
    const date = new Date(utcIso);
    if (isNaN(date.getTime())) {
      throw new Error('Invalid UTC date');
    }
    
    const dateFormatter = new Intl.DateTimeFormat('en-CA', {
      timeZone: shopTz,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
    
    const timeFormatter = new Intl.DateTimeFormat('en-GB', {
      timeZone: shopTz,
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
    
    const dateStr = dateFormatter.format(date);
    const timeStr = timeFormatter.format(date);
    
    // Parse hours and minutes from timeStr (HH:mm format)
    const [hours, minutes] = timeStr.split(':').map(Number);
    
    return {
      dateStr,
      timeStr,
      minutesSinceMidnight: hours * 60 + minutes,
    };
  } catch (error) {
    console.error('[shopTime] utcToShopLocal error:', error);
    return {
      dateStr: formatDateToLabel(new Date()),
      timeStr: '00:00',
      minutesSinceMidnight: 0,
    };
  }
}

/**
 * Validate that a UTC instant falls on the expected shop-local date
 * Used to validate client-sent UTC times
 * 
 * @param utcIso - UTC ISO string to validate
 * @param expectedDateStr - Expected date in YYYY-MM-DD format (shop-local)
 * @param shopTz - IANA timezone
 * @returns true if the UTC time falls on the expected shop-local date
 */
export function validateUtcOnDate(
  utcIso: string,
  expectedDateStr: string,
  shopTz: string
): boolean {
  const actualDateStr = getShopDayLabel(utcIso, shopTz);
  return actualDateStr === expectedDateStr;
}

/**
 * Validate that a string is a valid IANA timezone
 * 
 * @param tz - Timezone string to validate
 * @returns true if valid IANA timezone
 */
export function isValidIanaTimezone(tz: string): boolean {
  if (!tz || typeof tz !== 'string') return false;
  
  try {
    Intl.DateTimeFormat('en-US', { timeZone: tz });
    return true;
  } catch {
    return false;
  }
}

/**
 * Get "today" label in shop timezone
 *
 * Chronomancer R-PAST-5: routes through `clock.now()` so edge-function
 * tests that set `FIXED_TIME` env var see a stable "today" value.
 * Previously used raw `new Date()`, which ignored the clock abstraction
 * — edge-function tests that relied on FIXED_TIME to simulate "now"
 * got the real wall-clock date here, producing false passes or
 * non-deterministic failures.
 *
 * @param shopTz - IANA timezone
 * @returns Today's date in YYYY-MM-DD format (shop-local)
 */
export function getTodayInShopTz(shopTz: string): string {
  return getShopDayLabel(clock.now().toISOString(), shopTz);
}

/**
 * Check if a UTC time is in the past
 *
 * Chronomancer R-PAST-5: routes through `clock.nowMs()` for the same
 * test-determinism reason as `getTodayInShopTz`.
 *
 * @param utcIso - UTC ISO string to check
 * @returns true if the time is in the past
 */
export function isTimeInPast(utcIso: string): boolean {
  const targetTime = new Date(utcIso).getTime();
  const now = clock.nowMs();
  return targetTime < now;
}

/**
 * Add minutes to a UTC ISO string
 * 
 * @param utcIso - UTC ISO string
 * @param minutes - Minutes to add (can be negative)
 * @returns New UTC ISO string
 */
export function addMinutesUtc(utcIso: string, minutes: number): string {
  const date = new Date(utcIso);
  date.setTime(date.getTime() + minutes * 60 * 1000);
  return date.toISOString();
}

/**
 * Get technician's hours for a specific day.
 *
 * CANONICAL SOURCE (TSF-A2 — mirrors DMC-G2):
 *   `tech.hours` jsonb is the SOLE canonical scheduling source. The
 *   per-weekday `{ open, close }` map is keyed by either numeric
 *   weekday ("0"–"6") or day-name ("Sunday"–"Saturday"); both are
 *   supported. When the resolved day has no entry — or when `hours`
 *   itself is null/empty — this function returns null. Callers MUST
 *   handle the null return as "tech not working that day."
 *
 * HISTORY (DMC-G1 + DMC-G2 + TSF-A2):
 *   Pre-DMC-G this helper (and its web-side mirror at
 *   `src/utils/shopTime.ts:getTechDayHours`) fell back to legacy
 *   `tech.shift_start` / `tech.shift_end` columns when the per-day
 *   hours entry was empty. DMC-G1 (migration
 *   `20260510120000_dmc_g1_backfill_shift_start_end.sql`) normalized
 *   any drifty rows by backfilling shift_start/end from the canonical
 *   `hours` envelope, leaving zero remaining drifts. DMC-G2 removed
 *   the fallback from the web-side helper so `hours` became the
 *   unambiguous SoT for scheduling. **TSF-A2 (this round) closes the
 *   edge-side gap** — pre-TSF-A2 the edge mirror still carried the
 *   legacy `if (tech.shift_start && tech.shift_end) return ...` block,
 *   which produced cross-side semantic divergence on tech-off-day
 *   detection (web treated empty `hours[day]` as "off"; edge treated
 *   it as "on flat-column-resolved schedule"). The two edge consumers
 *   (`split-ticket/index.ts:183-191` EOD overflow check; and
 *   `_shared/eodRolloverHandler.ts:249` next-working-day lookup) were
 *   already null-safe; no consumer migration was required.
 *
 *   The legacy columns remain on the schema (`TechnicianForm` continues
 *   to derive them from the hours envelope on every save — they're a
 *   denormalized convenience for non-scheduling readers like
 *   FormulaDebugPanel) but are no longer consulted here.
 *
 * @param tech - Technician object with hours record. The `shift_start`
 *   and `shift_end` fields are kept on the parameter type for back-
 *   compat with existing call sites that pass full Technician rows;
 *   they are NOT read by this function as of TSF-A2.
 * @param boardDateStr - Date in YYYY-MM-DD format (shop-local)
 * @returns Open/close times for the resolved day, or null if not working
 */
export function getTechDayHours(
  tech: {
    // Accept the canonical (both-required) shape and the looser TechnicianLike
    // shape (numeric-or-name keys, optional open/close), matching the web
    // helper (src/utils/shopTime.ts). The body validates `open && close`
    // before returning, so admitting the looser input never yields a partial
    // result.
    hours?: Record<number | string, { open?: string; close?: string }> | null;
    shift_start?: string | null;
    shift_end?: string | null;
  },
  boardDateStr: string
): { open: string; close: string } | null {
  // Parse the date to get day of week
  const [year, month, day] = boardDateStr.split('-').map(Number);
  const date = new Date(year, month - 1, day);

  const dayIndex = date.getDay(); // 0=Sunday, 1=Monday, ...
  const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  const dayName = dayNames[dayIndex];

  // TSF-A2: technicians.hours is the sole canonical scheduling source.
  // Per-day hours support both numeric keys ("0"–"6") and day-name keys
  // ("Sunday"–"Saturday"). When the resolved day has no entry, return
  // null — callers handle that as "tech not working." The pre-TSF-A2
  // fallback to tech.shift_start / tech.shift_end was removed after
  // DMC-G1's backfill normalized every drifty row; web-side DMC-G2
  // removed the same block from `src/utils/shopTime.ts`.
  const dayHoursByIndex = tech.hours?.[String(dayIndex)];
  const dayHoursByName = tech.hours?.[dayName];
  const dayHours = dayHoursByIndex || dayHoursByName;

  if (dayHours?.open && dayHours?.close) {
    return { open: dayHours.open, close: dayHours.close };
  }

  return null;
}

// Helper function to format Date to YYYY-MM-DD
function formatDateToLabel(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * An IANA zone name the runtime accepts, or null. oct20c (PR #13 audit, finding 1): a shop's zone is authoritative —
 * a missing or unknown one is never replaced by a default (a guessed zone moves every "today" and every minute of the
 * day), it is a failure the caller reports.
 */
export function validTimeZoneOrNull(tz: unknown): string | null {
  if (typeof tz !== 'string' || tz.trim() === '') return null;
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: tz });
    return tz;
  } catch {
    return null;
  }
}
